import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Pencil, Trash2, Copy, Check, Key, KeyRound } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTasks } from '../contexts/TaskContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamManagement = () => {
  const { user: currentUser } = useAuth();
  // Re-sync the global store after member changes so task views (which display
  // member names/assignments) stay consistent everywhere.
  const { refreshTasks } = useTasks();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'member'
  });
  const [credentialsSection, setCredentialsSection] = useState({});
  // shape: { [memberId]: { editing, newUsername, newPassword, saving, saved, error } }

  const toggleCredentialsEdit = (memberId, currentUsername) => {
    setCredentialsSection(prev => ({
      ...prev,
      [memberId]: {
        ...prev[memberId],
        editing: !prev[memberId]?.editing,
        newUsername: prev[memberId]?.editing ? '' : (currentUsername || ''),
        newPassword: '',
        saved: false,
        error: null,
      }
    }));
  };

  const handleCredentialsSave = async (member) => {
    const state = credentialsSection[member.id];
    const hasUsername = state?.newUsername && state.newUsername.trim();
    const hasPassword = state?.newPassword && state.newPassword.trim();
    
    // Validate: at least one field must be provided
    if (!hasUsername && !hasPassword) {
      setCredentialsSection(prev => ({
        ...prev,
        [member.id]: { ...prev[member.id], error: 'Enter username or password to update' }
      }));
      return;
    }
    
    // Validate password length if provided
    if (hasPassword && state.newPassword.length < 6) {
      setCredentialsSection(prev => ({
        ...prev,
        [member.id]: { ...prev[member.id], error: 'Password must be at least 6 characters' }
      }));
      return;
    }

    setCredentialsSection(prev => ({
      ...prev,
      [member.id]: { ...prev[member.id], saving: true, error: null }
    }));

    try {
      const token = sessionStorage.getItem('auth_token');
      const body = {};
      if (hasUsername) body.username = state.newUsername.trim();
      if (hasPassword) body.password = state.newPassword;
      
      const res = await fetch(`${API}/team-members/${member.id}/reset-credentials`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update credentials');
      }

      setCredentialsSection(prev => ({
        ...prev,
        [member.id]: { ...prev[member.id], saving: false, saved: true, editing: false, newUsername: '', newPassword: '' }
      }));
      
      // Refresh team members list to show updated username
      fetchTeamMembers();
      
      toast.success(`Credentials updated for ${member.name}`);

      setTimeout(() => {
        setCredentialsSection(prev => ({
          ...prev,
          [member.id]: { ...prev[member.id], saved: false }
        }));
      }, 3000);

    } catch (err) {
      setCredentialsSection(prev => ({
        ...prev,
        [member.id]: { ...prev[member.id], saving: false, error: err.message }
      }));
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team-members`);
      setTeamMembers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to load team members');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMember) {
        await axios.put(`${API}/team-members/${editingMember.id}`, formData);
        toast.success('Team member updated');
        setDialogOpen(false);
      } else {
        const response = await axios.post(`${API}/team-members`, formData);
        // Show credentials dialog with generated username and password
        setNewCredentials({
          name: response.data.name,
          username: response.data.username,
          password: response.data.generatedPassword
        });
        setDialogOpen(false);
        setCredentialsDialogOpen(true);
      }
      setEditingMember(null);
      resetForm();
      fetchTeamMembers();
      refreshTasks();
      window.dispatchEvent(new Event('refreshDashboard'));
    } catch (error) {
      console.error('Error saving team member:', error);
      toast.error('Failed to save team member');
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success('Copied to clipboard');
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email,
      role: member.role
    });
    setDialogOpen(true);
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Delete this team member? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/team-members/${memberId}`);
      toast.success('Team member deleted');
      fetchTeamMembers();
      refreshTasks();
      window.dispatchEvent(new Event('refreshDashboard'));
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast.error('Failed to delete team member');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', role: 'member' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="p-8 space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Team Management</h1>
          <p className="text-muted-foreground mt-1">Manage your team members</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => { resetForm(); setEditingMember(null); }}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">
                {editingMember ? 'Edit Team Member' : 'Add Team Member'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-sm font-semibold">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1.5 bg-white"
                  placeholder="e.g., Saurav"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Username will be auto-generated: {formData.name ? formData.name.toLowerCase().replace(/\s+/g, '') : 'username'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-1.5 bg-white"
                  placeholder="e.g., saurav@company.com"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Role *</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="mt-1.5 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.role === 'admin' ? 'Can manage all accounts, tasks, and team members' : 'Can only view and update their assigned tasks'}
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  {editingMember ? 'Update' : 'Add'} Team Member
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teamMembers.map((member) => (
          <motion.div
            key={member.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Card className="p-6 bg-white hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center text-xl font-bold">
                  {member.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">{member.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      member.role === 'admin' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {member.role === 'admin' ? 'Admin' : 'Member'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{member.email}</p>
                  {member.username && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <span className="font-medium">Username:</span> {member.username}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                      {member.taskCount} tasks
                    </span>
                    {member.laggingTaskCount > 0 && (
                      <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                        {member.laggingTaskCount} overdue
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t">
                <button
                  onClick={() => handleEdit(member)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                >
                  <Pencil className="h-4 w-4" />
                  <span className="text-sm font-medium">Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(member.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="text-sm font-medium">Delete</span>
                </button>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Account Credentials Section - Admin only */}
      {currentUser?.role === 'admin' && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Account Credentials</h2>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Admin only</span>
          </div>
          <div className="rounded-lg border border-border/60 overflow-x-auto bg-white">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Username</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Password</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.map((member) => {
                  const state = credentialsSection[member.id] || {};
                  return (
                    <tr key={member.id} className="border-t border-border/40 hover:bg-muted/20 transition-colors">
                      <td className="py-3 px-4 font-medium">{member.name}</td>
                      <td className="py-3 px-4">
                        {state.editing ? (
                          <input
                            type="text"
                            placeholder="Username"
                            value={state.newUsername || ''}
                            onChange={(e) => setCredentialsSection(prev => ({
                              ...prev,
                              [member.id]: { ...prev[member.id], newUsername: e.target.value, error: null }
                            }))}
                            className="border border-border rounded px-2 py-1 text-xs w-32 font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        ) : (
                          <span className="font-mono text-xs">{member.username || '—'}</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {state.editing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="New password (min 6 chars)"
                              value={state.newPassword || ''}
                              onChange={(e) => setCredentialsSection(prev => ({
                                ...prev,
                                [member.id]: { ...prev[member.id], newPassword: e.target.value, error: null }
                              }))}
                              className="border border-border rounded px-2 py-1 text-xs w-40 font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            <button
                              onClick={() => copyToClipboard(state.newPassword || '', 'pw-' + member.id)}
                              className="text-muted-foreground hover:text-foreground"
                              title="Copy password"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <span className="font-mono text-xs text-muted-foreground">••••••••</span>
                        )}
                        {state.error && (
                          <p className="text-xs text-red-500 mt-1">{state.error}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {state.editing ? (
                            <>
                              <button
                                onClick={() => handleCredentialsSave(member)}
                                disabled={state.saving}
                                className="text-xs bg-primary text-white px-3 py-1 rounded hover:bg-primary/90 disabled:opacity-50"
                              >
                                {state.saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={() => toggleCredentialsEdit(member.id, member.username)}
                                className="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => toggleCredentialsEdit(member.id, member.username)}
                                className="text-xs border border-border px-3 py-1 rounded hover:bg-muted transition-colors flex items-center gap-1"
                              >
                                <KeyRound className="w-3 h-3" />
                                Edit
                              </button>
                              {state.saved && (
                                <span className="text-xs text-green-500 flex items-center gap-1">
                                  <Check className="w-3 h-3" /> Saved
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Credentials Dialog - Shows after creating new team member */}
      <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Key className="h-6 w-6 text-primary" />
              Team Member Created
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 font-medium">
                Save these credentials now! The password won't be shown again.
              </p>
            </div>
            
            {newCredentials && (
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <p className="font-semibold">{newCredentials.name}</p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-muted-foreground">Username</Label>
                  <div className="flex items-center justify-between">
                    <p className="font-mono font-semibold">{newCredentials.username}</p>
                    <button
                      onClick={() => copyToClipboard(newCredentials.username, 'username')}
                      className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                    >
                      {copiedField === 'username' ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Label className="text-xs text-green-700">Password (save this!)</Label>
                  <div className="flex items-center justify-between">
                    <p className="font-mono font-bold text-lg">{newCredentials.password}</p>
                    <button
                      onClick={() => copyToClipboard(newCredentials.password, 'password')}
                      className="p-1.5 hover:bg-green-100 rounded transition-colors"
                    >
                      {copiedField === 'password' ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-green-700" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end pt-4 border-t">
              <Button 
                onClick={() => setCredentialsDialogOpen(false)}
                className="bg-primary hover:bg-primary/90"
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default TeamManagement;
